import { useEffect } from "react";
import { useRouter } from "next/router";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { altDataUpgradeDialogState, userDataState } from "@/lib/store";

/**
 * Access control for the Alt Data section.
 *
 * Alt Data is part of ORTEX Advanced and above (Advanced, Professional,
 * Institutional). The backend decides (`has_advanced_platform`) and puts the
 * answer on the `/API/v2/user` payload as `alt_data`; the menu shows the entry
 * to everyone and unentitled users get the upgrade dialog.
 *
 * This deliberately **fails closed**, which is the opposite default to
 * {@link useOptionsUiRedirect}. That hook guards pages a user is normally
 * entitled to see, so an unknown answer keeps them where they are; this one
 * guards a page nobody is entitled to by default, so an unknown answer must
 * not reveal it. Concretely, a backend that predates the flag omits
 * `alt_data`, and the section stays hidden rather than opening to everyone.
 *
 * The API enforces the same entitlement independently
 * (`interface.api.altdata` requires `PRODUCT_ALT_DATA`), so this is about
 * not showing a dead page — not the security boundary.
 */

export interface AltDataUserData {
    alt_data?: boolean;
}

/**
 * Does this user have the Alt Data entitlement?
 *
 * Returns `undefined` while the answer is not yet knowable (the user payload
 * has not arrived), so callers can hold rendering rather than flash a section
 * the user may not be allowed to see. Every decidable case returns a boolean.
 */
export function hasAltDataAccess(userData: AltDataUserData | null | undefined): boolean | undefined {
    if (!userData) return undefined;
    // Present-but-false and absent-entirely both mean "no". Absent covers a
    // backend older than the flag, and treating it as no is the fail-closed
    // half of the contract above.
    return !!userData.alt_data;
}

/**
 * Should the Alt Data page render its content?
 *
 * False until the entitlement is confirmed, so an unentitled user never sees
 * the section flash before the redirect lands.
 */
export function useAltDataVisible(): boolean {
    const userData = useRecoilValue(userDataState) as AltDataUserData | null | undefined;
    return hasAltDataAccess(userData) === true;
}

/**
 * Send users without the entitlement away from an Alt Data route and show
 * them the upgrade dialog instead of a dead page.
 *
 * Only acts once the payload has arrived and the answer is a definite "no" —
 * an undecidable state redirects nobody, it just renders nothing (see
 * {@link useAltDataVisible}), so a slow payload can never bounce an entitled
 * user off their own page.
 */
export function useAltDataRedirect(destination = "/") {
    const userData = useRecoilValue(userDataState) as AltDataUserData | null | undefined;
    const setUpgradeOpen = useSetRecoilState(altDataUpgradeDialogState);
    const router = useRouter();

    useEffect(() => {
        if (hasAltDataAccess(userData) === false) {
            setUpgradeOpen(true);
            router.replace(destination);
        }
        // router identity churns per render; path-relevant inputs only.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userData, destination]);
}
